import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import { Autocomplete, TextField } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useInstitution } from '../../components/InstitutionContext';
import { useAuth } from '../../components/AuthContext';
import { InstitutionStatsCards } from '../../components';
import api, { fetchInstitutions } from '../../services/api';

import { styled } from '@mui/material/styles';

const StyledBox = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  width: '100%',
  height: 400,
  marginTop: theme.spacing(8),
  borderRadius: (theme.vars || theme).shape.borderRadius,
  outline: '6px solid',
  outlineColor: 'hsla(220, 25%, 80%, 0.2)',
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.grey[200],
  boxShadow: '0 0 12px 8px hsla(220, 25%, 80%, 0.2)',
  backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || 'https://mui.com'}/static/screenshots/material-ui/getting-started/templates/dashboard.jpg)`,
  backgroundSize: 'cover',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(10),
    height: 700,
  },
  ...theme.applyStyles('dark', {
    boxShadow: '0 0 24px 12px hsla(210, 100%, 25%, 0.2)',
    backgroundImage: `url(${process.env.TEMPLATE_IMAGE_URL || 'https://mui.com'}/static/screenshots/material-ui/getting-started/templates/dashboard-dark.jpg)`,
    outlineColor: 'hsla(220, 20%, 42%, 0.1)',
    borderColor: (theme.vars || theme).palette.grey[700],
  }),
}));

export default function Hero() {
  const navigate = useNavigate();
  const { setSelectedInstitution } = useInstitution();
  const { user, isAuthenticated, setUser } = useAuth();
  const [institutions, setInstitutions] = React.useState([]);
  const [states, setStates] = React.useState([]);
  const [cities, setCities] = React.useState([]);
  const [selectedState, setSelectedState] = React.useState('');
  const [selectedCity, setSelectedCity] = React.useState('');
  const [institutionId, setInstitutionId] = React.useState('');

  React.useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchInstitutions();
        setInstitutions(data);
        const uniqueStates = [...new Set(data.map((i) => i.state_abbr))].sort();
        setStates(uniqueStates);
      } catch (err) {
        console.error('Failed to load institutions:', err);
      }
    };
    load();
  }, []);

  React.useEffect(() => {
    const filtered = selectedState
      ? institutions.filter((i) => i.state_abbr === selectedState)
      : institutions;
    const uniqueCities = [...new Set(filtered.map((i) => i.city_name))].sort();
    setCities(uniqueCities);
    setSelectedCity('');
  }, [selectedState, institutions]);


  const filteredInstitutions = React.useMemo(() => {
    return institutions.filter(
      (inst) =>
        (!selectedState || inst.state_abbr === selectedState) &&
        (!selectedCity || inst.city_name === selectedCity),
    );
  }, [institutions, selectedState, selectedCity]);

  const showDropdown = !isAuthenticated || !user?.userprofile?.institution;

  const handleExplore = async () => {
    if (!institutionId) return;
    setSelectedInstitution(institutionId);

    if (isAuthenticated && !user?.userprofile?.institution) {
      try {
        await api.put('/users/profile/', { profile: { institution: institutionId } });
        const resp = await api.get('/users/check/');
        if (resp.data.isAuthenticated) {
          setUser(resp.data.user);
        }
      } catch (err) {
        console.error('Failed to save institution:', err);
      }
    }

    navigate('/courses');
  };

  return (
    <Box
      id="hero"
      sx={(theme) => ({
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundImage:
          'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)',
        ...theme.applyStyles('dark', {
          backgroundImage:
            'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
        }),
      })}
    >
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 8, sm: 14 },
          pb: { xs: 4, sm: 8 },
        }}
      >
        <Stack
          spacing={2}
          useFlexGap
          sx={{ alignItems: 'center', width: { xs: '100%', sm: '100%' }, mx: 'auto' }}
        >
          <Typography
            variant="h1"
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              fontSize: 'clamp(2.75rem, 8vw, 3.5rem)',
              textAlign: 'center',
            }}
          >
            Empower&nbsp;your&nbsp;
            <Typography
              component="span"
              variant="h1"
              sx={(theme) => ({
                fontSize: 'inherit',
                color: 'primary.main',
                ...theme.applyStyles?.('dark', {
                  color: 'primary.light',
                }),
              })}
            >
              premed&nbsp;journey
            </Typography>
          </Typography>

          <Typography
            sx={{
              textAlign: 'center',
              color: 'text.secondary',
              width: { sm: '100%', md: '80%' },
            }}
          >
            Explore course reviews, connect with fellow premeds, get tutoring support, and discover research and clinical opportunities — all in one place.
          </Typography>

          {showDropdown ? (
            <>
              <Typography
                component="h2"
                variant="h4"
                gutterBottom
                sx={{ color: 'text.primary', mt: 4 }}
              >
                Select An Institution to Explore
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                useFlexGap
                sx={{ pt: 2, width: { xs: '100%', sm: '700px' }, mx: 'auto', justifyContent: 'center' }}
              >
                <Autocomplete
                  options={states}
                  sx={{ minWidth: 200 }}
                  value={selectedState || null}
                  onChange={(e, newVal) => {
                    setSelectedState(newVal || '');
                    setInstitutionId('');
                  }}
                  componentsProps={{
                    clearIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                    popupIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select state..." size="small" />
                  )}
                />
                <Autocomplete
                  options={cities}
                  sx={{ minWidth: 200 }}
                  value={selectedCity || null}
                  onChange={(e, newVal) => {
                    setSelectedCity(newVal || '');
                    setInstitutionId('');
                  }}
                  componentsProps={{
                    clearIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                    popupIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select city..." size="small" />
                  )}
                />
                <Autocomplete
                  options={filteredInstitutions}
                  getOptionLabel={(o) => o.display_name}
                  sx={{ minWidth: 200 }}
                  value={
                    filteredInstitutions.find((inst) => String(inst.id) === institutionId) ||
                    null
                  }
                  onChange={(e, newVal) => setInstitutionId(newVal?.id ? String(newVal.id) : '')}
                  componentsProps={{
                    clearIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                    popupIndicator: {
                      sx: {
                        p: 0,
                        width: '1.5rem',
                        height: '1.5rem',
                        border: 0,
                        bgcolor: 'transparent',
                        '&:hover': { bgcolor: 'transparent' },
                        '&:active': { bgcolor: 'transparent' },
                      },
                    },
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Select institution..." size="small" />
                  )}
                />
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{ minWidth: 'fit-content' }}
                  onClick={handleExplore}
                >
                  Explore
                </Button>
              </Stack>
            </>
          ) : (
            <InstitutionStatsCards />
          )}
        </Stack>

        {/* <StyledBox id="image" /> */}
      </Container>
    </Box>
  );
}
